import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  ExclamationCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { financeiroApi } from '../../../services/api';
import type { Client, PaginatedResponse } from '../../../types';

const BeneficiariosList: React.FC = () => {
  const navigate = useNavigate();
  const [beneficiarios, setBeneficiarios] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    loadBeneficiarios();
  }, [currentPage, pageSize, searchTerm, tipoFilter]);

  const loadBeneficiarios = async () => {
    try {
      setLoading(true);
      const params: Record<string, any> = {
        page: currentPage,
        page_size: pageSize,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }
      if (tipoFilter) {
        params.tipo = tipoFilter;
      }

      const response = await financeiroApi.getBeneficiarios(params);
      setBeneficiarios(response.results);
      setTotalCount(response.count || 0);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar beneficiários');
      console.error('Error loading beneficiarios:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir o beneficiário "${name}"?`)) {
      return;
    }

    try {
      await financeiroApi.deleteBeneficiario(id);
      setSuccessMessage('Beneficiário excluído com sucesso!');
      setTimeout(() => setSuccessMessage(null), 3000);
      loadBeneficiarios();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao excluir beneficiário');
      setTimeout(() => setError(null), 5000);
    }
  };

  const formatDocument = (document: string | null | undefined) => {
    if (!document) return '-';

    // Remove non-digits
    const cleaned = document.replace(/\D/g, '');

    // CPF
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }

    // CNPJ
    if (cleaned.length === 14) {
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }

    return document;
  };

  const getTipoLabel = (tipo: string | undefined) => {
    const tipos: Record<string, string> = {
      supplier: 'Fornecedor',
      customer: 'Cliente',
      both: 'Cliente e Fornecedor',
    };
    return tipos[tipo || ''] || tipo || '-';
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const clearFilters = () => {
    setSearchTerm('');
    setTipoFilter('');
    setCurrentPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-secondary-900">Beneficiários</h1>
          <p className="text-xs text-secondary-600">
            Gerenciar fornecedores, clientes e outros beneficiários
          </p>
        </div>
        <button
          onClick={() => navigate('/financeiro/beneficiarios/new')}
          className="btn-primary whitespace-nowrap"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          Novo Beneficiário
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <ExclamationCircleIcon className="h-4 w-4 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
              <input
                type="text"
                placeholder="Buscar por nome, CPF/CNPJ..."
                className="input-field pl-10"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <select
              className="input-field"
              value={tipoFilter}
              onChange={(e) => {
                setTipoFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Todos os tipos</option>
              <option value="supplier">Fornecedor</option>
              <option value="customer">Cliente</option>
              <option value="both">Cliente e Fornecedor</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-secondary-600">Carregando beneficiários...</p>
          </div>
        ) : beneficiarios.length === 0 ? (
          <div className="text-center py-8">
            <ExclamationCircleIcon className="h-12 w-12 text-secondary-400 mx-auto" />
            <p className="mt-2 text-secondary-600">Nenhum beneficiário encontrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Nome</th>
                  <th className="px-2 py-1.5 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">CPF/CNPJ</th>
                  <th className="px-2 py-1.5 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-secondary-100">
                {beneficiarios.map((beneficiario) => (
                  <tr key={beneficiario.id} className="hover:bg-secondary-25">
                    <td className="px-2 py-1.5 text-xs font-medium text-secondary-900">
                      {beneficiario.name}
                    </td>
                    <td className="px-2 py-1.5 text-xs text-secondary-500">
                      {formatDocument(beneficiario.cpf_cnpj)}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <div className="flex justify-end space-x-1">
                        <button
                          onClick={() => navigate(`/financeiro/beneficiarios/${beneficiario.id}/edit`)}
                          className="text-primary-600 hover:text-primary-900 p-0.5"
                          title="Editar"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(beneficiario.id, beneficiario.name)}
                          className="text-red-600 hover:text-red-900 p-0.5"
                          title="Excluir"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {beneficiarios.length > 0 && (
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Mostrando {(currentPage - 1) * pageSize + 1} a{' '}
              {Math.min(currentPage * pageSize, totalCount)} de {totalCount} beneficiários
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page: number;
                  if (totalPages <= 5) {
                    page = i + 1;
                  } else if (currentPage <= 3) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i;
                  } else {
                    page = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>

              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BeneficiariosList;
